<template>
  <div class="pt-4">
    <span class="text-zt-primary-200 text-zt-base">
       {{ $t('message.searchComponent.document') }}
    </span>
    <div class="mt-2 border border-zt-dark-300 rounded px-2 pt-1 relative">
      <label
        class="px-2 text-zt-dark-300 text-zt-xs bg-white absolute top--negative__8"
        >
        {{ $t('message.searchComponent.folder') }}
        </label
      >
      <div>
        <MultiSelect
          :value="folderSelected"
          @input="setFolderSelected"
          :options="folderOpts"
          :searchable="true"
          label="name"
          :multiple="true"
          group-values="childs"
          group-label="name"
          :group-select="true"
          :internal-search="false"
          :clear-on-select="true"
          :close-on-select="false"
          :preserve-search="false"
          :show-labels="false"
          :show-no-options="false"
          placeholder=""
          track-by="name"
          data-vs-button="folder-selected"
        >
          <template slot="noResult"
            ><span class="multiselect__single">{{
              $t('message.searchComponent.not_founded')
            }}</span></template
          >
          <template slot="maxElements"
            ><span class="multiselect__single">{{
              $t('message.searchComponent.max_elements')
            }}</span></template
          >
          <template #tag="{ remove, option }">
            <CustomChip @click="remove(option)">{{ option.name }}</CustomChip>
          </template>
        </MultiSelect>
      </div>
    </div>
    <div class="flex py-4">
      <div class="flex-1">
        <div class="pr-1">
          <v-date-picker
            v-model="datePickerStart.date"
            :masks="masks"
            @input="setInputStartPicker"
            :max-date="maxStartDate"
            :popover="{ visibility: 'click' }"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <div class="border border-zt-dark-300 rounded px-2 pt-1 relative">
                <label
                  class="px-2 text-zt-dark-300 text-zt-xs bg-white absolute top--negative__8"
                  >{{ languages.date.from }}</label
                >
                <div>
                  <div>
                    <input
                      v-on="inputEvents"
                      :value="inputValue"
                      type="text"
                      class="outline-none py-2 w-full"
                      data-vs-input="input-events"
                    />
                  </div>
                </div>
              </div>
            </template>
          </v-date-picker>
        </div>
      </div>
      <div class="flex-1">
        <div class="pl-1">
          <v-date-picker
            v-model="datePickerEnd.date"
            :masks="masks"
            @input="setInputEndPicker"
            :max-date="nowDate"
            :min-date="datePickerStart.date"
            :popover="{ visibility: 'click' }"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <div class="border border-zt-dark-300 rounded px-2 pt-1 relative">
                <label
                  class="px-2 text-zt-dark-300 text-zt-xs bg-white absolute top--negative__8"
                  >{{ languages.date.to }}</label
                >
                <div>
                  <div>
                    <input
                      v-on="inputEvents"
                      :value="inputValue"
                      type="text"
                      class="outline-none py-2 w-full"
                    />
                  </div>
                </div>
              </div>
            </template>
          </v-date-picker>
        </div>
      </div>
      <div v-show="isResetDatesVisible" class="px-2 inline-flex items-center">
        <button
          @click="clearDateInputs"
          class="text-zt-primary-200 text-zt-xl focus:outline-none"
          data-vs-button="clear-date"
        >
          <zt-icon name="times-solid" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { filter, has, map, pick } from 'lodash'
import MultiSelect from 'vue-multiselect'
import CustomChip from './CustomChip.vue'
import { addFilters } from './utils'
import moment from 'moment'

const formatInput = (date, format) => {
  return moment(date).format(format)
}

const sendGTMCustomEvent = (event) => {
  window.dataLayer.push({
    event,
    action: 'Click',
    value: 500,
  })
}

export default {
  name: 'SearchDocumentSection',
  inject: ['store', 'setFilters', 'setHistoryParams', 'existHistoryProperty'],
  components: {
    MultiSelect,
    CustomChip,
  },
  data() {
    return {
      masks: {
        input: 'DD/MM/YYYY',
      },
      nowDate: new Date(),
      folderSelected: [],
      datePickerStart: {
        date: '',
        dateInverterStart: '',
        menu: false,
      },
      datePickerEnd: {
        date: '',
        dateInverterEnd: '',
        menu: false,
      },
      languages: {
        date: {
          from: this.$t('message.searchComponent.signed.from'),
          to: this.$t('message.searchComponent.signed.to')
        },
      },
    }
  },
  computed: {
    folderOpts() {
      return filter(this.store.foldersItems, { type: 'folder' })
    },
    setInputStartDate() {
      if (!this.datePickerStart.date) {
        return null
      }
      return formatInput(this.datePickerStart.date, 'YYYY-MM-DD')
    },
    setInputEndDate() {
      if (!this.datePickerEnd.date) {
        return null
      }
      return formatInput(this.datePickerEnd.date, 'YYYY-MM-DD')
    },
    maxStartDate() {
      if (this.datePickerEnd.date) {
        return this.datePickerEnd.date
      }

      return this.nowDate
    },
    isResetDatesVisible() {
      return !!this.datePickerStart.date || !!this.datePickerEnd.date
    },
  },
  created() {
    if (this.existHistoryProperty('signStatus')) {
      this.statusSelected = this.store.history.params.signStatus
    }

    if (this.existHistoryProperty('subFolderId_two')) {
      this.folderSelected = this.store.history.params.subFolderId_two
    }

    if (this.existHistoryProperty('signDateFrom')) {
      const start = this.store.history.params.signDateFrom
      this.datePickerStart.date = moment(start).toDate()
    }

    if (this.existHistoryProperty('signDateTo')) {
      const end = this.store.history.params.signDateTo
      this.datePickerEnd.date = moment(end).toDate()
    }
  },
  methods: {
    setFolderSelected(value) {
      sendGTMCustomEvent('zt-gtm-documents-search-folderdocumentfilter')
      this.folderSelected = value
      this.setStoreFilter(['subFolderId', map(this.folderSelected, 'id')])

      const { history: { params } } = this.store
      this.setHistoryParams(
        addFilters(
          { ...params },
          ['subFolderId_two', ...[this.folderSelected]]
        )
      )
    },
    setStatusSelected(value) {
      this.statusSelected = value
      this.setStoreFilter(['signStatus', [...this.statusSelected]])

      const { history: { params } } = this.store
      this.setHistoryParams(
        addFilters(
          { ...params },
          ['signStatus', ...[this.folderSelected]]
        )
      )
    },
    setStoreFilter(...args) {
      const { filters } = this.store
      const newFilters = addFilters({ ...filters }, ...args)
      this.setFilters(newFilters)
    },
    setInputStartPicker() {
      this.changePicker(this.setInputStartDate, 'signDateFrom')
    },
    setInputEndPicker() {
      this.changePicker(this.setInputEndDate, 'signDateTo')
    },
    changePicker(value, key) {
      this.setStoreFilter([key, value])

      const { history: { params } } = this.store
      this.setHistoryParams(
        addFilters(
          { ...params },
          [key, ...[value]]
        )
      )
    },
    clearDateInputs() {
      this.datePickerStart.date = ''
      this.datePickerEnd.date = ''
      this.setStoreFilter(['signDateFrom', ''], ['signDateTo', ''])

      const { history: { params } } = this.store
      this.setHistoryParams(
        addFilters(
          { ...params },
          ['signDateFrom', ''],
          ['signDateTo', ''],
        )
      )
    },
    cleanFilters() {
      this.folderSelected = []
    }
  }
}
</script>

<style scoped lang="scss">
  ::v-deep .multiselect__content-wrapper {
    box-shadow: 0 5px 5px -3px rgba(0,0,0,.2), 0 8px 10px 1px rgba(0,0,0,.14), 0 3px 14px 2px rgba(0,0,0,.12);
  }
</style>
